import React, { useState, useEffect } from 'react';
import { rssAPI, categoriesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Rss, Plus, RefreshCw, Download, Trash2, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';

export default function AdminRSSFeeds() {
  const [feeds, setFeeds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);
  const [fetchingId, setFetchingId] = useState(null);
  const [importingFeedId, setImportingFeedId] = useState(null);
  const [newFeed, setNewFeed] = useState({ name: '', url: '', category_id: '' });

  useEffect(() => {
    loadFeeds();
    categoriesAPI.list().then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const loadFeeds = async () => {
    try {
      const res = await rssAPI.list();
      setFeeds(res.data.feeds || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newFeed.name || !newFeed.url) { toast.error('Name and URL required'); return; }
    try {
      await rssAPI.create(newFeed);
      toast.success('Feed added!');
      setShowAdd(false);
      setNewFeed({ name: '', url: '', category_id: '' });
      loadFeeds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add feed');
    }
  };

  const handleFetch = async (feedId) => {
    setFetchingId(feedId);
    try {
      const res = await rssAPI.fetch(feedId);
      setFetchResult(res.data);
      setImportingFeedId(feedId);
      toast.success(`Fetched ${res.data.item_count} items!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fetch failed');
    } finally {
      setFetchingId(null);
    }
  };

  const handleImport = async (items) => {
    if (!importingFeedId) return;
    try {
      const res = await rssAPI.import(importingFeedId, items);
      toast.success(`Imported ${res.data.imported} articles as drafts!`);
      setFetchResult(null);
      setImportingFeedId(null);
    } catch (err) {
      toast.error('Import failed');
    }
  };

  const handleDelete = async (feedId) => {
    if (!window.confirm('Delete this feed?')) return;
    try {
      await rssAPI.delete(feedId);
      toast.success('Feed deleted');
      loadFeeds();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">RSS Feeds</h1>
          <p className="text-sm text-[#5B616E] mt-1">Aggregate news from external sources</p>
        </div>
        <Button className="bg-[#121316] text-white hover:bg-[#1B1D22]" onClick={() => setShowAdd(true)}>
          <Plus className="w-4 h-4 mr-2" /> Add Feed
        </Button>
      </div>

      {/* Add Feed Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add RSS Feed</DialogTitle>
            <DialogDescription>Enter the feed URL to start aggregating news.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium block mb-1">Feed Name</label>
              <input
                value={newFeed.name}
                onChange={e => setNewFeed({ ...newFeed, name: e.target.value })}
                placeholder="BBC News"
                className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Feed URL</label>
              <input
                data-testid="rss-url-input"
                value={newFeed.url}
                onChange={e => setNewFeed({ ...newFeed, url: e.target.value })}
                placeholder="https://feeds.bbci.co.uk/news/rss.xml"
                className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white font-mono"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Category (optional)</label>
              <select
                value={newFeed.category_id}
                onChange={e => setNewFeed({ ...newFeed, category_id: e.target.value })}
                className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">None</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <Button data-testid="rss-add-button" type="submit" className="w-full bg-[#121316] text-white hover:bg-[#1B1D22]">
              Add Feed
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Feeds List */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : feeds.length === 0 ? (
        <Card className="bg-white border-[#E7E0D6] p-12 text-center">
          <Rss className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
          <p className="text-[#5B616E]">No RSS feeds configured. Add your first feed to start aggregating news.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {feeds.map(feed => (
            <Card key={feed.id} className="bg-white border-[#E7E0D6]">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Rss className="w-4 h-4 text-[#B89B5E]" />
                      <h3 className="font-medium text-sm">{feed.name}</h3>
                      <Badge variant="secondary" className="text-[10px]">{feed.is_active ? 'Active' : 'Inactive'}</Badge>
                    </div>
                    <p className="text-xs text-[#5B616E] mt-1 font-mono truncate">{feed.url}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-[#5B616E]">
                      {feed.last_fetched && <span>Last fetched: {new Date(feed.last_fetched).toLocaleString()}</span>}
                      {feed.items_count > 0 && <span>{feed.items_count} items</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="rss-fetch-now-button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleFetch(feed.id)}
                      disabled={fetchingId === feed.id}
                    >
                      <RefreshCw className={`w-3 h-3 mr-1 ${fetchingId === feed.id ? 'animate-spin' : ''}`} />
                      {fetchingId === feed.id ? 'Fetching...' : 'Fetch Now'}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(feed.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Fetch Results Dialog */}
      <Dialog open={!!fetchResult} onOpenChange={() => { setFetchResult(null); setImportingFeedId(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fetched Items ({fetchResult?.item_count || 0})</DialogTitle>
            <DialogDescription>Select items to import as draft articles.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {fetchResult?.items?.slice(0, 20).map((item, idx) => (
              <div key={idx} className="border border-[#E7E0D6] rounded-lg p-3">
                <h4 className="text-sm font-medium">{item.title}</h4>
                <p className="text-xs text-[#5B616E] mt-1 line-clamp-2">{item.summary}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-[#5B616E]">{item.published}</span>
                  <Button size="sm" variant="outline" onClick={() => handleImport([item])}>
                    <Download className="w-3 h-3 mr-1" /> Import
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {fetchResult?.items?.length > 0 && (
            <Button className="w-full mt-4 bg-[#121316] text-white" onClick={() => handleImport(fetchResult.items.slice(0, 20))}>
              Import All ({Math.min(20, fetchResult.items.length)} articles)
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
